import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save, ArrowLeft, Plus, Trash2, GripVertical, Eye, EyeOff,
  Globe, ExternalLink, ChevronUp, ChevronDown, Image, Link2,
  Instagram, Facebook, Twitter, Linkedin, Youtube, Phone, Mail, X,
  Building2, Users, Handshake, Home, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { bioLinksApi } from '@/api/bioLinks';
import { apiV1 } from '@/api/client';
import { ImagePicker } from '@/components/ImagePicker';

const THEME_PRESETS = [
  { name: 'Dark', bg: '#0f172a', text: '#ffffff', button: '#3b82f6' },
  { name: 'Light', bg: '#ffffff', text: '#1f2937', button: '#3b82f6' },
  { name: 'Teal', bg: '#144733', text: '#ffffff', button: '#ffffff' },
  { name: 'Purple', bg: '#7c3aed', text: '#ffffff', button: '#ffffff' },
  { name: 'Rose', bg: '#e11d48', text: '#ffffff', button: '#ffffff' },
  { name: 'Gradient Blue', bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', text: '#ffffff', button: '#ffffff' },
  { name: 'Gradient Sunset', bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', text: '#ffffff', button: '#ffffff' },
];

const SOCIAL_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
  youtube: Youtube,
  phone: Phone,
  email: Mail,
  website: Globe,
};

// Mobile Preview Component
function MobilePreview({ bio }) {
  const theme = bio?.theme || {};
  const profile = bio?.profile || {};
  const links = bio?.links || [];
  
  const bgStyle = theme.background_type === 'gradient' && theme.background_gradient
    ? { background: theme.background_gradient }
    : { backgroundColor: theme.background_color || '#0f172a' };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  };

  return (
    <div className="relative mx-auto" style={{ width: '280px' }}>
      {/* Phone Frame */}
      <div className="relative rounded-[2.5rem] border-[8px] border-gray-800 bg-gray-800 shadow-xl overflow-hidden">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-800 rounded-b-2xl z-20" />
        
        {/* Screen Content */}
        <div 
          className="relative h-[500px] overflow-y-auto"
          style={bgStyle}
        >
          <div className="pt-12 pb-8 px-4 flex flex-col items-center min-h-full">
            {/* Profile - Avatar or Logo */}
            {profile.avatar_shape === 'logo' ? (
              // Full Logo Display
              profile.avatar_url ? (
                <img 
                  src={profile.avatar_url} 
                  alt={profile.display_name || 'Logo'}
                  className="max-w-[200px] max-h-20 object-contain mb-3"
                />
              ) : (
                <div 
                  className="w-32 h-12 mb-3 rounded flex items-center justify-center text-xs"
                  style={{ backgroundColor: theme.button_color || '#3b82f6', color: theme.button_text_color || '#fff' }}
                >
                  Logo
                </div>
              )
            ) : (
              // Circle Avatar Display
              <Avatar className="w-20 h-20 mb-3 ring-2 ring-white/20">
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback 
                  className="text-lg"
                  style={{ backgroundColor: theme.button_color || '#3b82f6', color: theme.button_text_color || '#fff' }}
                >
                  {getInitials(profile.display_name)}
                </AvatarFallback>
              </Avatar>
            )}
            
            <h2 
              className="text-lg font-bold mb-1 text-center"
              style={{ color: theme.text_color || '#ffffff' }}
            >
              {profile.display_name || 'Your Name'}
            </h2>
            
            {profile.bio && (
              <p 
                className="text-sm text-center mb-4 opacity-80 max-w-[200px]"
                style={{ color: theme.text_color || '#ffffff' }}
              >
                {profile.bio}
              </p>
            )}

            {/* Links */}
            <div className="w-full space-y-3 mt-2">
              {links.filter(l => l.visible !== false).sort((a, b) => a.order - b.order).map((link) => (
                link.type === 'header' ? (
                  <p 
                    key={link.id}
                    className="text-xs font-medium uppercase tracking-wider text-center pt-2 opacity-60"
                    style={{ color: theme.text_color || '#ffffff' }}
                  >
                    {link.title}
                  </p>
                ) : (
                  <div
                    key={link.id}
                    className={`w-full py-3 px-4 text-center font-medium transition-transform hover:scale-[1.02] cursor-pointer ${
                      theme.button_style === 'outline' ? 'border-2' : ''
                    } ${theme.button_radius || 'rounded-lg'}`}
                    style={{
                      backgroundColor: theme.button_style === 'outline' ? 'transparent' : (theme.button_color || '#3b82f6'),
                      borderColor: theme.button_color || '#3b82f6',
                      color: theme.button_style === 'outline' ? (theme.button_color || '#3b82f6') : (theme.button_text_color || '#ffffff'),
                      boxShadow: theme.show_shadow ? '0 4px 12px rgba(0,0,0,0.15)' : 'none'
                    }}
                  >
                    <span className="flex items-center justify-center gap-2">
                      {link.icon && <span>{link.icon}</span>}
                      {link.title}
                    </span>
                    {link.clicks > 0 && (
                      <span className="text-xs opacity-60 ml-2">({link.clicks})</span>
                    )}
                  </div>
                )
              ))}
              
              {links.length === 0 && (
                <p className="text-center text-sm opacity-50" style={{ color: theme.text_color }}>
                  Add your first link
                </p>
              )}
            </div>

            {/* Social Icons */}
            {bio?.social_links?.length > 0 && (
              <div className="flex gap-4 mt-6">
                {bio.social_links.filter(l => l.visible !== false).map((social) => {
                  const Icon = SOCIAL_ICONS[social.social_platform] || Globe;
                  return (
                    <div
                      key={social.id}
                      className="w-10 h-10 rounded-full flex items-center justify-center opacity-80 hover:opacity-100"
                      style={{ backgroundColor: theme.button_color || '#3b82f6' }}
                    >
                      <Icon className="w-5 h-5" style={{ color: theme.button_text_color || '#fff' }} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BioEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bio, setBio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('links');
  const [addLinkOpen, setAddLinkOpen] = useState(false);
  const [newLink, setNewLink] = useState({ type: 'link', title: '', url: '', icon: '' });
  
  // Tracking reference data
  const [referenceData, setReferenceData] = useState({
    regions: [],
    offices: [],
    agents: [],
    partnerships: []
  });
  const [filteredOffices, setFilteredOffices] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [officeTypeFilter, setOfficeTypeFilter] = useState('all');

  useEffect(() => {
    if (id) {
      fetchBio();
      fetchReferenceData();
    }
  }, [id]);
  
  // Filter offices based on region and office type
  useEffect(() => {
    let filtered = referenceData.offices;
    
    const tracking = bio?.tracking || {};
    
    // Filter by region
    if (tracking.region_id) {
      filtered = filtered.filter(o => o.region_id === tracking.region_id);
    }
    
    // Filter by office type
    if (officeTypeFilter !== 'all') {
      filtered = filtered.filter(o => o.office_type === officeTypeFilter);
    }
    
    setFilteredOffices(filtered);
    
    // Reset office if no longer valid
    if (tracking.office_id) {
      const stillValid = filtered.some(o => o.id === tracking.office_id);
      if (!stillValid) {
        updateTracking('office_id', null);
        updateTracking('office_name', null);
        updateTracking('office_code', null);
      }
    }
  }, [bio?.tracking?.region_id, officeTypeFilter, referenceData.offices]);
  
  // Filter agents based on office/region
  useEffect(() => {
    let filtered = referenceData.agents;
    const tracking = bio?.tracking || {};
    
    if (tracking.office_id) {
      filtered = filtered.filter(a => a.office_id === tracking.office_id);
    } else if (tracking.region_id) {
      const regionOfficeIds = referenceData.offices
        .filter(o => o.region_id === tracking.region_id)
        .map(o => o.id);
      filtered = filtered.filter(a => regionOfficeIds.includes(a.office_id));
    }
    
    setFilteredAgents(filtered);
    
    // Reset agent if no longer valid
    if (tracking.agent_id) {
      const stillValid = filtered.some(a => a.id === tracking.agent_id);
      if (!stillValid) {
        updateTracking('agent_id', null);
        updateTracking('agent_name', null);
        updateTracking('agent_code', null);
      }
    }
  }, [bio?.tracking?.office_id, bio?.tracking?.region_id, referenceData.agents, referenceData.offices]);

  const fetchReferenceData = async () => {
    try {
      const response = await apiV1.get('/reference-data/options');
      if (response.data.success) {
        setReferenceData({
          regions: response.data.data.region || [],
          offices: response.data.data.office || [],
          agents: response.data.data.agent || [],
          partnerships: response.data.data.partnership || [],
        });
        setFilteredOffices(response.data.data.office || []);
        setFilteredAgents(response.data.data.agent || []);
      }
    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  };
  
  const updateTracking = (field, value) => {
    setBio(prev => ({
      ...prev,
      tracking: {
        ...prev?.tracking,
        [field]: value
      }
    }));
  };
  
  const handleTrackingSelect = (field, value, options, nameField, codeField) => {
    const selected = options.find(o => o.id === value);
    updateTracking(field, value || null);
    updateTracking(nameField, selected?.name || null);
    if (codeField) {
      updateTracking(codeField, selected?.code || null);
    }
  };

  const fetchBio = async () => {
    setLoading(true);
    try {
      const data = await bioLinksApi.get(id);
      if (data.success) {
        setBio(data.data);
      } else {
        toast.error('Bio page not found');
        navigate('/bio');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await bioLinksApi.update(id, {
        profile: bio.profile,
        links: bio.links,
        social_links: bio.social_links,
        theme: bio.theme,
        tracking: bio.tracking,
        meta_title: bio.meta_title,
        meta_description: bio.meta_description
      });
      if (data.success) {
        toast.success('Saved!');
        setBio(data.data);
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      const data = await bioLinksApi.publish(id);
      if (data.success) {
        toast.success('Published!');
        setBio(data.data);
      }
    } catch (error) {
      toast.error('Failed to publish');
    }
  };

  const handleUnpublish = async () => {
    try {
      const data = await bioLinksApi.unpublish(id);
      if (data.success) {
        toast.success('Unpublished');
        setBio(data.data);
      }
    } catch (error) {
      toast.error('Failed to unpublish');
    }
  };

  const addLink = () => {
    if (!newLink.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    const link = {
      id: `link_${Date.now()}`,
      type: newLink.type,
      title: newLink.title,
      url: newLink.url,
      icon: newLink.icon,
      order: (bio.links?.length || 0) + 1,
      visible: true,
      clicks: 0
    };
    setBio({ ...bio, links: [...(bio.links || []), link] });
    setNewLink({ type: 'link', title: '', url: '', icon: '' });
    setAddLinkOpen(false);
  };

  const updateLink = (linkId, updates) => {
    setBio({
      ...bio,
      links: bio.links.map(l => l.id === linkId ? { ...l, ...updates } : l)
    });
  };

  const deleteLink = (linkId) => {
    setBio({
      ...bio,
      links: bio.links.filter(l => l.id !== linkId)
    });
  };

  const moveLink = (linkId, direction) => {
    const links = [...bio.links].sort((a, b) => a.order - b.order);
    const idx = links.findIndex(l => l.id === linkId);
    if ((direction === -1 && idx === 0) || (direction === 1 && idx === links.length - 1)) return;
    
    const newIdx = idx + direction;
    const [removed] = links.splice(idx, 1);
    links.splice(newIdx, 0, removed);
    
    const reordered = links.map((l, i) => ({ ...l, order: i + 1 }));
    setBio({ ...bio, links: reordered });
  };

  const applyThemePreset = (preset) => {
    const isGradient = preset.bg.includes('gradient');
    setBio({
      ...bio,
      theme: {
        ...bio.theme,
        background_type: isGradient ? 'gradient' : 'solid',
        background_color: isGradient ? '#0f172a' : preset.bg,
        background_gradient: isGradient ? preset.bg : null,
        text_color: preset.text,
        button_color: preset.button,
        button_text_color: preset.button === '#ffffff' ? '#1f2937' : '#ffffff'
      }
    });
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="bio-editor-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-7"><Skeleton className="h-[500px]" /></div>
          <div className="col-span-5"><Skeleton className="h-[500px]" /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bio-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/bio')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold">{bio?.profile?.display_name}</h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>/b/{bio?.slug}</span>
              <Badge variant={bio?.status === 'published' ? 'default' : 'secondary'}>
                {bio?.status}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {bio?.status === 'published' ? (
            <>
              <Button variant="outline" asChild>
                <a href={`/b/${bio.slug}`} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Live
                </a>
              </Button>
              <Button variant="outline" onClick={handleUnpublish}>
                <EyeOff className="w-4 h-4 mr-2" />
                Unpublish
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={handlePublish}>
              <Globe className="w-4 h-4 mr-2" />
              Publish
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} data-testid="save-bio-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left: Editor */}
        <div className="col-span-7 space-y-4">
          <Card>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <CardHeader className="pb-0">
                <TabsList className="grid grid-cols-4">
                  <TabsTrigger value="links">Links</TabsTrigger>
                  <TabsTrigger value="profile">Profile</TabsTrigger>
                  <TabsTrigger value="tracking">Tracking</TabsTrigger>
                  <TabsTrigger value="style">Style</TabsTrigger>
                </TabsList>
              </CardHeader>
              <CardContent className="pt-4">
                {/* Links Tab */}
                <TabsContent value="links" className="mt-0 space-y-4">
                  {/* Links List */}
                  <div className="space-y-2">
                    {bio?.links?.sort((a, b) => a.order - b.order).map((link) => (
                      <div
                        key={link.id}
                        className="flex items-center gap-2 p-3 border rounded-lg bg-card"
                      >
                        <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                        
                        {link.type === 'header' ? (
                          <Input
                            value={link.title}
                            onChange={(e) => updateLink(link.id, { title: e.target.value })}
                            className="flex-1 font-medium text-sm"
                            placeholder="Section header"
                          />
                        ) : (
                          <div className="flex-1 space-y-2">
                            <Input
                              value={link.title}
                              onChange={(e) => updateLink(link.id, { title: e.target.value })}
                              placeholder="Link title"
                              className="font-medium"
                            />
                            <Input
                              value={link.url}
                              onChange={(e) => updateLink(link.id, { url: e.target.value })}
                              placeholder="https://..."
                              className="text-sm"
                            />
                          </div>
                        )}
                        
                        <div className="flex items-center gap-1">
                          <Switch
                            checked={link.visible !== false}
                            onCheckedChange={(v) => updateLink(link.id, { visible: v })}
                          />
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveLink(link.id, -1)}>
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveLink(link.id, 1)}>
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => deleteLink(link.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add Link */}
                  <Dialog open={addLinkOpen} onOpenChange={setAddLinkOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full" data-testid="add-link-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Link
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Link</DialogTitle>
                        <DialogDescription>Add a new link or header</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div>
                          <Label>Type</Label>
                          <Select value={newLink.type} onValueChange={(v) => setNewLink({ ...newLink, type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="link">Link Button</SelectItem>
                              <SelectItem value="header">Section Header</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Title</Label>
                          <Input
                            value={newLink.title}
                            onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                            placeholder={newLink.type === 'header' ? 'Section name' : 'Link title'}
                          />
                        </div>
                        {newLink.type === 'link' && (
                          <>
                            <div>
                              <Label>URL</Label>
                              <Input
                                value={newLink.url}
                                onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                                placeholder="https://..."
                              />
                            </div>
                            <div>
                              <Label>Icon (emoji, optional)</Label>
                              <Input
                                value={newLink.icon}
                                onChange={(e) => setNewLink({ ...newLink, icon: e.target.value })}
                                placeholder="🔗"
                                className="w-20"
                              />
                            </div>
                          </>
                        )}
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setAddLinkOpen(false)}>Cancel</Button>
                        <Button onClick={addLink}>Add</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </TabsContent>

                {/* Profile Tab */}
                <TabsContent value="profile" className="mt-0 space-y-4">
                  <ImagePicker
                    label="Avatar / Logo"
                    value={bio?.profile?.avatar_url || ''}
                    onChange={(url) => setBio({ ...bio, profile: { ...bio.profile, avatar_url: url }})}
                    placeholder="Select or upload avatar or logo image"
                    aspectRatio={bio?.profile?.avatar_shape === 'logo' ? '16/9' : '1/1'}
                    folder={id ? `bio_links/${id}` : 'bio_links'}
                    testId="bio-avatar-picker"
                  />
                  <div>
                    <Label>Avatar Display Style</Label>
                    <Select
                      value={bio?.profile?.avatar_shape || 'circle'}
                      onValueChange={(v) => setBio({ ...bio, profile: { ...bio.profile, avatar_shape: v }})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="circle">Circle (Avatar)</SelectItem>
                        <SelectItem value="logo">Full Logo (Rectangular)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Circle is ideal for profile photos, Logo for company branding
                    </p>
                  </div>
                  <div>
                    <Label>Display Name</Label>
                    <Input
                      value={bio?.profile?.display_name || ''}
                      onChange={(e) => setBio({ ...bio, profile: { ...bio.profile, display_name: e.target.value }})}
                    />
                  </div>
                  <div>
                    <Label>Bio</Label>
                    <Textarea
                      value={bio?.profile?.bio || ''}
                      onChange={(e) => setBio({ ...bio, profile: { ...bio.profile, bio: e.target.value }})}
                      placeholder="Tell visitors about yourself..."
                      rows={3}
                    />
                  </div>
                  <Separator />
                  <div>
                    <Label>SEO Title</Label>
                    <Input
                      value={bio?.meta_title || ''}
                      onChange={(e) => setBio({ ...bio, meta_title: e.target.value })}
                      placeholder={bio?.profile?.display_name}
                    />
                  </div>
                  <div>
                    <Label>SEO Description</Label>
                    <Textarea
                      value={bio?.meta_description || ''}
                      onChange={(e) => setBio({ ...bio, meta_description: e.target.value })}
                      rows={2}
                    />
                  </div>
                </TabsContent>

                {/* Tracking Tab */}
                <TabsContent value="tracking" className="mt-0 space-y-4">
                  <div className="text-sm text-muted-foreground mb-4">
                    Link this bio page to your organization structure for analytics and reporting.
                  </div>
                  
                  <div className="space-y-4">
                    {/* Region */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-primary" />
                        Region
                      </Label>
                      <Select
                        value={bio?.tracking?.region_id || "none"}
                        onValueChange={(value) => handleTrackingSelect('region_id', value === "none" ? null : value, referenceData.regions, 'region_name', 'region_code')}
                      >
                        <SelectTrigger data-testid="tracking-region-select">
                          <SelectValue placeholder="Select region (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {referenceData.regions.map(region => (
                            <SelectItem key={region.id} value={region.id}>
                              {region.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Office Type Filter */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        Office Type Filter
                      </Label>
                      <Select
                        value={officeTypeFilter}
                        onValueChange={setOfficeTypeFilter}
                      >
                        <SelectTrigger data-testid="tracking-office-type-select">
                          <SelectValue placeholder="Filter by office type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="branch">Branch</SelectItem>
                          <SelectItem value="franchise">Franchise</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Filter the office list by type
                      </p>
                    </div>

                    {/* Office */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-primary" />
                        Office
                        {filteredOffices.length > 0 && (
                          <span className="text-xs text-muted-foreground">
                            ({filteredOffices.length} available)
                          </span>
                        )}
                      </Label>
                      <Select
                        value={bio?.tracking?.office_id || "none"}
                        onValueChange={(value) => handleTrackingSelect('office_id', value === "none" ? null : value, filteredOffices, 'office_name', 'office_code')}
                      >
                        <SelectTrigger data-testid="tracking-office-select">
                          <SelectValue placeholder="Select office (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {filteredOffices.map(office => (
                            <SelectItem key={office.id} value={office.id}>
                              {office.name}
                              {office.office_type && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  ({office.office_type})
                                </span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {bio?.tracking?.region_id && filteredOffices.length === 0 && (
                        <p className="text-xs text-amber-600">
                          No offices found for the selected region
                        </p>
                      )}
                    </div>

                    {/* Agent */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-primary" />
                        Agent
                        {filteredAgents.length > 0 && (
                          <span className="text-xs text-muted-foreground">
                            ({filteredAgents.length} available)
                          </span>
                        )}
                      </Label>
                      <Select
                        value={bio?.tracking?.agent_id || "none"}
                        onValueChange={(value) => handleTrackingSelect('agent_id', value === "none" ? null : value, filteredAgents, 'agent_name', 'agent_code')}
                      >
                        <SelectTrigger data-testid="tracking-agent-select">
                          <SelectValue placeholder="Select agent (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {filteredAgents.map(agent => (
                            <SelectItem key={agent.id} value={agent.id}>
                              {agent.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {bio?.tracking?.office_id && filteredAgents.length === 0 && (
                        <p className="text-xs text-amber-600">
                          No agents found for the selected office
                        </p>
                      )}
                    </div>

                    {/* Partnership */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Handshake className="h-4 w-4 text-primary" />
                        Partnership
                      </Label>
                      <Select
                        value={bio?.tracking?.partnership_id || "none"}
                        onValueChange={(value) => handleTrackingSelect('partnership_id', value === "none" ? null : value, referenceData.partnerships, 'partnership_name', 'partnership_code')}
                      >
                        <SelectTrigger data-testid="tracking-partnership-select">
                          <SelectValue placeholder="Select partnership (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {referenceData.partnerships.map(partnership => (
                            <SelectItem key={partnership.id} value={partnership.id}>
                              {partnership.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Listing Reference */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Home className="h-4 w-4 text-primary" />
                        Listing Reference
                      </Label>
                      <Input
                        placeholder="e.g., PROP-2024-001"
                        value={bio?.tracking?.listing_ref || ''}
                        onChange={(e) => updateTracking('listing_ref', e.target.value || null)}
                        data-testid="tracking-listing-ref-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Property or listing reference number for tracking
                      </p>
                    </div>
                  </div>
                  
                  {/* Current Tracking Summary */}
                  {(bio?.tracking?.region_name || bio?.tracking?.office_name || bio?.tracking?.agent_name) && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <Label className="text-xs uppercase text-muted-foreground">Current Assignment</Label>
                        <div className="flex flex-wrap gap-2">
                          {bio?.tracking?.region_name && (
                            <Badge variant="secondary" className="text-xs">
                              <MapPin className="h-3 w-3 mr-1" />
                              {bio.tracking.region_name}
                            </Badge>
                          )}
                          {bio?.tracking?.office_name && (
                            <Badge variant="secondary" className="text-xs">
                              <Building2 className="h-3 w-3 mr-1" />
                              {bio.tracking.office_name}
                            </Badge>
                          )}
                          {bio?.tracking?.agent_name && (
                            <Badge variant="secondary" className="text-xs">
                              <Users className="h-3 w-3 mr-1" />
                              {bio.tracking.agent_name}
                            </Badge>
                          )}
                          {bio?.tracking?.partnership_name && (
                            <Badge variant="secondary" className="text-xs">
                              <Handshake className="h-3 w-3 mr-1" />
                              {bio.tracking.partnership_name}
                            </Badge>
                          )}
                          {bio?.tracking?.listing_ref && (
                            <Badge variant="secondary" className="text-xs">
                              <Home className="h-3 w-3 mr-1" />
                              {bio.tracking.listing_ref}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </TabsContent>

                {/* Style Tab */}
                <TabsContent value="style" className="mt-0 space-y-4">
                  {/* Theme Presets */}
                  <div>
                    <Label className="mb-2 block">Theme Presets</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {THEME_PRESETS.map((preset) => (
                        <button
                          key={preset.name}
                          onClick={() => applyThemePreset(preset)}
                          className="p-3 rounded-lg border hover:border-primary transition-colors text-center"
                          style={{ 
                            background: preset.bg,
                            color: preset.text
                          }}
                        >
                          <span className="text-xs font-medium">{preset.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Custom Colors */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Background Color</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={bio?.theme?.background_color || '#0f172a'}
                          onChange={(e) => setBio({ ...bio, theme: { ...bio.theme, background_type: 'solid', background_color: e.target.value }})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={bio?.theme?.background_color || '#0f172a'}
                          onChange={(e) => setBio({ ...bio, theme: { ...bio.theme, background_color: e.target.value }})}
                          className="font-mono"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Text Color</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={bio?.theme?.text_color || '#ffffff'}
                          onChange={(e) => setBio({ ...bio, theme: { ...bio.theme, text_color: e.target.value }})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={bio?.theme?.text_color || '#ffffff'}
                          onChange={(e) => setBio({ ...bio, theme: { ...bio.theme, text_color: e.target.value }})}
                          className="font-mono"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Button Color</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={bio?.theme?.button_color || '#3b82f6'}
                          onChange={(e) => setBio({ ...bio, theme: { ...bio.theme, button_color: e.target.value }})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={bio?.theme?.button_color || '#3b82f6'}
                          onChange={(e) => setBio({ ...bio, theme: { ...bio.theme, button_color: e.target.value }})}
                          className="font-mono"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Button Text</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={bio?.theme?.button_text_color || '#ffffff'}
                          onChange={(e) => setBio({ ...bio, theme: { ...bio.theme, button_text_color: e.target.value }})}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={bio?.theme?.button_text_color || '#ffffff'}
                          onChange={(e) => setBio({ ...bio, theme: { ...bio.theme, button_text_color: e.target.value }})}
                          className="font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Button Style */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Button Style</Label>
                      <Select 
                        value={bio?.theme?.button_style || 'filled'}
                        onValueChange={(v) => setBio({ ...bio, theme: { ...bio.theme, button_style: v }})}
                      >
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="filled">Filled</SelectItem>
                          <SelectItem value="outline">Outline</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Button Radius</Label>
                      <Select 
                        value={bio?.theme?.button_radius || 'rounded-lg'}
                        onValueChange={(v) => setBio({ ...bio, theme: { ...bio.theme, button_radius: v }})}
                      >
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rounded-none">Square</SelectItem>
                          <SelectItem value="rounded-md">Rounded</SelectItem>
                          <SelectItem value="rounded-lg">More Rounded</SelectItem>
                          <SelectItem value="rounded-full">Pill</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={bio?.theme?.show_shadow !== false}
                      onCheckedChange={(v) => setBio({ ...bio, theme: { ...bio.theme, show_shadow: v }})}
                    />
                    <Label>Show button shadows</Label>
                  </div>
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>

        {/* Right: Mobile Preview */}
        <div className="col-span-5">
          <div className="sticky top-20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-sm text-muted-foreground">Mobile Preview</h3>
              {bio?.status === 'published' && (
                <Button variant="ghost" size="sm" asChild>
                  <a href={`/b/${bio.slug}`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    View Live
                  </a>
                </Button>
              )}
            </div>
            <MobilePreview bio={bio} />
          </div>
        </div>
      </div>
    </div>
  );
}
